
import React from 'react';
import { Ball, SimulationConfig, SpaceSpiralRoundConfig } from '@/types';
import { AUDIO } from '@/services/AudioService';

export const SpaceSpiralLogic = {
  init: (
    effectiveCfg: SimulationConfig,
    centerX: number,
    centerY: number,
    currentRoundRef: React.MutableRefObject<number>,
    roundStartTimeRef: React.MutableRefObject<number>,
    roundOverridesRef: React.MutableRefObject<SpaceSpiralRoundConfig | null>,
    resetRounds: boolean,
    createBall: (overrides: Partial<Ball>) => Ball,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } },
    now: number = Date.now()
  ): Ball[] => {
    if (effectiveCfg.spaceSpiralEnableRounds) {
        if (resetRounds) {
            currentRoundRef.current = 0;
            roundStartTimeRef.current = now;
            roundOverridesRef.current = null;
        }
    }

    if (effectiveCfg.spaceSpiralEnableRounds && effectiveCfg.spaceSpiralRoundConfigs && effectiveCfg.spaceSpiralRoundConfigs.length > 0) {
        const roundIdx = currentRoundRef.current % effectiveCfg.spaceSpiralRoundConfigs.length;
        const roundCfg = effectiveCfg.spaceSpiralRoundConfigs[roundIdx];
        if (roundCfg) {
            roundOverridesRef.current = roundCfg;
            effectiveCfg.spaceSpiralMassBox = roundCfg.massBox;
            effectiveCfg.spaceSpiralForwardThrust = roundCfg.forwardThrust;
            effectiveCfg.spaceSpiralThrustDuration = roundCfg.thrustDuration;
            effectiveCfg.spaceSpiralThrustAngle = roundCfg.thrustAngle;
            effectiveCfg.spaceSpiralGravityPower = roundCfg.gravityPower;
            effectiveCfg.spaceSpiralAirResistance = roundCfg.airResistance;
            effectiveCfg.spaceSpiralRestitution = roundCfg.restitution;
            effectiveCfg.spaceSpiralBoxRestitution = roundCfg.boxRestitution;
        }
    } else {
        roundOverridesRef.current = null;
    }

    const spiralBalls: Ball[] = [];
    const count = effectiveCfg.spaceSpiralBoxCount || 100;
    const spacing = effectiveCfg.spaceSpiralBoxSpacing !== undefined ? effectiveCfg.spaceSpiralBoxSpacing : 30;
    const startWidth = effectiveCfg.spaceSpiralBoxWidth || effectiveCfg.spaceSpiralBoxSize;
    const startHeight = effectiveCfg.spaceSpiralBoxHeight || effectiveCfg.spaceSpiralBoxSize;
    const scale = effectiveCfg.spaceSpiralScale || 1.0;
    const mass = effectiveCfg.spaceSpiralMassBox || 50;
    const color = effectiveCfg.spaceSpiralBoxColor || '#3b82f6';
    
    const maxScale = effectiveCfg.spaceSpiralMaxBoxScale !== undefined ? effectiveCfg.spaceSpiralMaxBoxScale : 1.0;
    const minScale = effectiveCfg.spaceSpiralMinBoxScale !== undefined ? effectiveCfg.spaceSpiralMinBoxScale : 0.3;
    const rotationOffset = (effectiveCfg.spaceSpiralRotation || 0) * (Math.PI / 180);
    const boxRotationOffset = (effectiveCfg.spaceSpiralBoxRotation || 0) * (Math.PI / 180);
    
    let currentR = (250 * scale); 
    let currentTheta = -Math.PI / 2 + rotationOffset; 
    
    for (let i = 0; i < count; i++) {
        const progress = i / count;
        const sizeFactor = maxScale - (progress * (maxScale - minScale)); 
        const w = startWidth * scale * sizeFactor;
        const h = startHeight * scale * sizeFactor;
        const x = currentR * Math.cos(currentTheta);
        const y = currentR * Math.sin(currentTheta);
        const angle = currentTheta + Math.PI / 2 + boxRotationOffset;
        
        let boxColor = color;
        if (effectiveCfg.spaceSpiralColorMode === 'RAINBOW') {
            const hue = (i * 5) % 360;
            boxColor = `hsl(${hue}, 100%, 50%)`;
        } else if (effectiveCfg.spaceSpiralColorMode === 'ALTERNATING') {
            boxColor = i % 2 === 0 ? (effectiveCfg.spaceSpiralColorA || '#3b82f6') : (effectiveCfg.spaceSpiralColorB || '#ef4444');
        } else if (effectiveCfg.spaceSpiralColorMode === 'SINGLE') {
            boxColor = effectiveCfg.spaceSpiralColorA || '#3b82f6';
        } else if (effectiveCfg.spaceSpiralColorMode === 'GRADIENT') {
            boxColor = effectiveCfg.spaceSpiralColorA || '#3b82f6'; 
        }
        
        spiralBalls.push(createBall({
            id: `spiral-box-${i}`,
            pos: { x, y },
            vel: { x: 0, y: 0 },
            radius: Math.max(w, h) / 2,
            mass: mass,
            shape: 'SQUARE',
            color: boxColor,
            style: effectiveCfg.spaceSpiralBoxStyle || 'MATTE',
            originalPos: { x, y },
            originalAngle: angle,
            angle: angle,
            dimensions: { x: w, y: h },
            isDebris: false,
            isBox: true
        }));
        
        const dTheta = (w + spacing) / currentR; 
        currentTheta += dTheta;
        const nextProgress = (i + 1) / count;
        currentR = (250 * scale) * (1.0 - nextProgress * 0.95);
    }
    
    const { pos, vel } = getInitialBallState(0, 1);
    const mainBall = createBall({
        id: 'hero-ball',
        pos,
        vel,
        radius: effectiveCfg.ballRadius,
        mass: effectiveCfg.spaceSpiralMassBall || 1,
        color: effectiveCfg.ballColor,
        shape: effectiveCfg.ballShape || 'CIRCLE', 
        style: effectiveCfg.ballStyle,
        spawnTime: now,
        isBox: false
    });
    
    return [mainBall, ...spiralBalls];
  },

  render: (ctx: CanvasRenderingContext2D, balls: Ball[], config: SimulationConfig, rgbStr: string, drawBall: any) => {
      balls.forEach(b => drawBall(ctx, b, config, rgbStr));
  },

  handleRoundLogic: (now: number, roundStartTime: number, effectiveCfg: SimulationConfig, balls: Ball[], centerX: number, centerY: number, currentRoundRef: any, setIsPlaying: any, initSimulation: any) => {
    const roundElapsed = (now - roundStartTime) / 1000;
    const timeLimit = effectiveCfg.spaceSpiralRoundTimeLimit || 60;
    const heroBall = balls.find(b => b.id === 'hero-ball');
    let shouldRestart = false, victory = false;
    if (heroBall) {
        const distToCenter = Math.sqrt((heroBall.pos.x)**2 + (heroBall.pos.y)**2);
        const speed = Math.sqrt(heroBall.vel.x**2 + heroBall.vel.y**2);
        if (distToCenter < 10) { victory = true; shouldRestart = true; }
        else if (roundElapsed > timeLimit) shouldRestart = true;
        else if (roundElapsed > 5 && speed < 0.2) shouldRestart = true;
    }
    if (shouldRestart) {
        if (victory) AUDIO.playSoundEffect('Explosion', effectiveCfg, 2.0);
        currentRoundRef.current++;
        setTimeout(() => initSimulation(), victory ? 1000 : 100);
        return true;
    }
    return false;
  }
};
